import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Scissors, FileAudio, FileVideo, Trash2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import StatusPill from "@/components/ui/StatusPill";
import { UploadImportPanel } from "@/components/snippets/UploadImportPanel";
import { MediaWorkbench } from "@/components/snippets/MediaWorkbench";
import { fmtTime } from "@/components/snippets/snippetUtil";
import { appConfirm } from "@/components/AppDialogHost";

const MEDIA_STATUS = { uploaded: "draft", transcribing: "pending", transcribed: "approved" };

export default function SnippetStudio() {
  const [mediaList, setMediaList] = useState([]);
  const [costs, setCosts] = useState({ transcribe: 2, highlights: 2, render: 3 });
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [media, setMedia] = useState(null);
  const [clips, setClips] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const r = await api.get("/snippets/media");
      setMediaList(r.data.media || []);
      if (r.data.costs) setCosts(r.data.costs);
    } catch { /* station not selected */ }
    setLoaded(true);
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setMedia(null); setClips([]); return; }
    try {
      const [m, c] = await Promise.all([
        api.get(`/snippets/media/${id}`),
        api.get("/snippets/clips", { params: { media_id: id } }),
      ]);
      setMedia(m.data);
      setClips(c.data.clips || []);
    } catch { setMedia(null); }
  }, []);

  useEffect(() => {
    loadList();
    api.get("/snippets/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [loadList]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const onMediaAdded = (doc) => { loadList(); setSelectedId(doc.media_id); };

  const deleteMedia = async (m, e) => {
    e.stopPropagation();
    if (!(await appConfirm("All of its clips will be removed too.", { title: `Delete "${m.filename}"?` }))) return;
    await api.delete(`/snippets/media/${m.media_id}`);
    toast.success("Media removed");
    if (selectedId === m.media_id) setSelectedId("");
    loadList();
  };

  const refreshBoth = () => { loadDetail(selectedId); loadList(); };

  return (
    <div className="space-y-6" data-testid="snippet-studio-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-60">
          <div className="flex items-center gap-2">
            <Scissors size={18} className="text-brand-amber" />
            <h1 className="type-section-heading text-ink-primary" style={{ fontSize: 20 }}>AI Social Snippet Studio</h1>
          </div>
          <p className="type-body text-ink-muted mt-1">
            Upload a show, interview or podcast — AI finds the shareable moments and renders branded social videos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted" data-testid="snip-costs-chip">
            // TRANSCRIBE {costs.transcribe} CR · HIGHLIGHTS {costs.highlights} CR · RENDER {costs.render} CR
          </span>
          <UploadImportPanel onDone={onMediaAdded} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="type-section-heading text-ink-primary">Media library</h3>
              <button onClick={loadList} className="text-ink-muted hover:text-ink-primary" data-testid="snip-media-refresh">
                <RefreshCw size={13} />
              </button>
            </div>
            {!loaded ? null : mediaList.length === 0 ? (
              <div className="type-body text-ink-muted py-4" data-testid="snip-media-empty">
                Nothing here yet — upload a recording or import a podcast episode to get started.
              </div>
            ) : (
              <div className="space-y-1.5">
                {mediaList.map((m) => (
                  <button key={m.media_id} onClick={() => setSelectedId(m.media_id)}
                    className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      selectedId === m.media_id ? "border-amber-500/50 bg-amber-500/5" : "border-line/40 hover:bg-white/5"}`}
                    data-testid={`snip-media-row-${m.media_id}`}>
                    {m.kind === "video"
                      ? <FileVideo size={15} className="text-sky-400 shrink-0" />
                      : <FileAudio size={15} className="text-brand-amber shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="type-list-title text-ink-primary truncate">{m.filename}</div>
                      <div className="type-list-subtitle text-ink-muted">
                        {fmtTime(m.duration)} · {m.highlight_count || 0} highlights · {m.clip_count || 0} clips
                      </div>
                    </div>
                    <StatusPill status={MEDIA_STATUS[m.status] || "draft"}>{m.status}</StatusPill>
                    <span onClick={(e) => deleteMedia(m, e)} className="text-ink-muted hover:text-red-400 cursor-pointer"
                      data-testid={`snip-media-delete-${m.media_id}`}>
                      <Trash2 size={13} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          {media ? (
            <MediaWorkbench
              media={media}
              clips={clips}
              costs={costs}
              templates={templates}
              onRefreshMedia={() => refreshBoth()}
              onClipsChanged={() => refreshBoth()}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-line p-10 text-center" data-testid="snip-workbench-empty">
              <Scissors size={28} className="mx-auto text-ink-muted mb-3" />
              <div className="type-section-heading text-ink-primary mb-1">Pick a recording to start</div>
              <div className="type-body text-ink-muted">
                Select media on the left, then transcribe it, let AI score the best moments, and render 9:16 / 1:1 / 16:9 social videos.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
